import { useCallback, useMemo, useState } from 'react';
import {
	DrawnSignatureSource,
	EidEasySigningMethod,
} from '@dealstack/shared/lib/common/eideasy/signingMethods';
import { SignerContactType } from '@dealstack/shared/lib/generated';
import { MAX_BULK_SIGN_BATCH_SIZE } from '@dealstack/shared/lib/common/eideasy/bulkSigning';
import {
	EidEasySignStatus,
	type BulkProgress,
} from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal.types';
import { useBulkFtnSign } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useBulkFtnSign';
import { useBulkOtpSign } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useBulkOtpSign';
import { DOCUMENT_NOT_BULK_SIGNABLE_ERROR_KEY } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useEidEasySign.utils';
import type {
	BulkSignTransport,
	BulkSignEndpointAction,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/bulkSignTransport';

/**
 * DRAW is excluded from the bulk method picker for the passwordless authorised
 * signatory flow. This does NOT remove the drawn/visual signature step —
 * `confirmSignature` still collects it and sends it as `drawnSignatureBase64`,
 * which the backend requires for every bulk method (see `PublicBulkPrepareDto`).
 */
export const PUBLIC_BULK_ALLOWED_METHODS = [EidEasySigningMethod.FTN, EidEasySigningMethod.OTP];

/**
 * What a round will sign. Modelled explicitly because the wire format is ambiguous: omitting
 * `selectedDocumentWorkflowInstanceIds` tells the server to sign everything it can, so a
 * missing selection must never be confused with an unconfigured round.
 */
type BulkSignTarget = { mode: 'all' } | { mode: 'selection'; instanceIds: string[] };

interface UsePublicBulkEidEasySignOptions {
	token: string;
	/**
	 * The documents that can still be bulk signed, live. A round is re-checked against this
	 * when it is submitted, not only when the signatory pressed the button.
	 */
	selectableInstanceIds: string[];
	onComplete?: () => void;
	/**
	 * The server refused the selection as stale. The caller should refresh its document list:
	 * the held selection can no longer succeed, so retrying it unchanged fails identically.
	 */
	onSelectionRejected?: () => void;
}

function buildProgrammeEndpoint(token: string, path: string): string {
	return `/public-signing/programme${path}?token=${encodeURIComponent(token)}`;
}

export const usePublicBulkEidEasySign = ({
	token,
	selectableInstanceIds,
	onComplete,
	onSelectionRejected,
}: UsePublicBulkEidEasySignOptions) => {
	const [status, setStatus] = useState<EidEasySignStatus>(EidEasySignStatus.IDLE);
	// FTN/OTP bulk signing never surfaces a challenge code (a Smart-ID concept),
	// but the shared modal expects the prop; it is always null.
	const challengeCode: string | null = null;
	const [error, setError] = useState<string | null>(null);
	const [otpVerifying, setOtpVerifying] = useState(false);
	const [otpCodeError, setOtpCodeError] = useState<string | null>(null);
	const [progress, setProgress] = useState<BulkProgress | null>(null);
	const [pendingMethod, setPendingMethod] = useState<EidEasySigningMethod | null>(null);
	const [capturedSignature, setCapturedSignature] = useState<{
		base64: string;
		source: DrawnSignatureSource;
	} | null>(null);
	const [target, setTarget] = useState<BulkSignTarget | null>(null);

	const transport: BulkSignTransport = useMemo(
		() => ({
			buildEndpoint: (action: BulkSignEndpointAction, jobId?: string) => {
				switch (action) {
					case 'initiate':
						return buildProgrammeEndpoint(token, '/bulk-sign/initiate');
					case 'signed':
						return buildProgrammeEndpoint(token, `/bulk-sign/${jobId ?? ''}/signed`);
					case 'status':
						return buildProgrammeEndpoint(token, `/bulk-sign/${jobId ?? ''}/status`);
					case 'prepare':
						return buildProgrammeEndpoint(token, '/bulk-prepare');
					case 'complete':
						return buildProgrammeEndpoint(token, '/bulk-complete');
				}
			},
			// Every request is unauthenticated (programme-token auth): a 401 must reach
			// this flow's own error handling, never makeRequest's login redirect.
			requestOptions: { skipLoginRedirect: true },
		}),
		[token]
	);

	const handleError = useCallback(
		(msg: string) => {
			setError(msg);
			setProgress(null);
			// A stale selection is the one error the signatory cannot clear by trying again —
			// the same ids would be refused the same way until the list is refetched.
			if (msg === DOCUMENT_NOT_BULK_SIGNABLE_ERROR_KEY) {
				onSelectionRejected?.();
			}
		},
		[onSelectionRejected]
	);

	const handleComplete = useCallback(() => {
		setStatus(EidEasySignStatus.DONE);
		setProgress(null);
		onComplete?.();
	}, [onComplete]);

	// Direct (non-polling) flow — the same path the admin bulk FTN sign uses
	// (prepare → sign in the eID Easy tab → complete). The async/Temporal job
	// path prepared the eID Easy batch with show_visual:false and a pre-stamped
	// PDF, which eID Easy's fi-ftn-advanced-signature/complete rejects; the direct
	// path sends show_visual:true + per-file visual_coordinates and serves the
	// stamp via the custom-signature-image webhook, exactly like single-doc FTN.
	const ftn = useBulkFtnSign({
		transport,
		signMode: 'direct',
		sentryFlowTag: 'public-bulk-ftn',
		queryKeyPrefix: 'public-bulk-sign-job',
		onStatusChange: setStatus,
		onError: handleError,
		onProgress: setProgress,
		onComplete: handleComplete,
	});

	const otp = useBulkOtpSign({
		transport,
		sentryFlowTag: 'public-bulk-otp',
		onStatusChange: setStatus,
		onError: handleError,
		onComplete: handleComplete,
		onVerifyingChange: setOtpVerifying,
		onCodeError: setOtpCodeError,
	});

	/**
	 * `instanceIds` is always the full set about to be signed. `isExplicitSelection` says
	 * whether the signatory chose it: an explicit selection goes on the wire so the server
	 * signs exactly those, while "sign all" sends nothing and lets the server resolve the set
	 * from the token (and defer anything past one batch).
	 */
	const startBulkSign = useCallback(
		(instanceIds: string[], { isExplicitSelection = false } = {}) => {
			// Dropped before the guards below, not after them: a refused start must leave no
			// target at all. Carrying the previous round's over would let the error step's
			// "try again" submit it.
			setTarget(null);
			if (instanceIds.length === 0) {
				setError('eid_easy_signing.error_no_documents_selected');
				setStatus(EidEasySignStatus.ERROR);
				return;
			}
			// The selection UI caps this already; this is the belt to the server's braces, and
			// gives the same copy without a round trip. Not applied to "sign all", whose
			// overflow the server defers rather than refuses.
			if (isExplicitSelection && instanceIds.length > MAX_BULK_SIGN_BATCH_SIZE) {
				setError('eid_easy_signing.error_too_many_documents');
				setStatus(EidEasySignStatus.ERROR);
				return;
			}
			setTarget(isExplicitSelection ? { mode: 'selection', instanceIds } : { mode: 'all' });
			setError(null);
			setProgress(null);
			setStatus(EidEasySignStatus.METHOD_SELECT);
		},
		[]
	);

	/**
	 * The round as it stands now, not as it stood at the click. A round spans a method pick, a
	 * signature capture and — for OTP — a trip to another app for the code, and the documents
	 * query refetches on window focus, so the list can move under an open modal. Sending an id
	 * the server has since dropped loses the whole round to a refusal.
	 */
	const liveTarget = useMemo((): BulkSignTarget | null => {
		if (target?.mode !== 'selection') {
			return target;
		}
		const selectable = new Set(selectableInstanceIds);
		return {
			mode: 'selection',
			instanceIds: target.instanceIds.filter((id) => selectable.has(id)),
		};
	}, [target, selectableInstanceIds]);

	const signRequestFields = useMemo(
		() =>
			liveTarget?.mode === 'selection'
				? { selectedDocumentWorkflowInstanceIds: liveTarget.instanceIds }
				: {},
		[liveTarget]
	);

	/** Every document the signatory chose has gone. `{}` here would read as "sign everything". */
	const hasNothingLeftToSign =
		liveTarget?.mode === 'selection' && liveTarget.instanceIds.length === 0;

	const selectMethod = useCallback((method: EidEasySigningMethod) => {
		if (PUBLIC_BULK_ALLOWED_METHODS.includes(method)) {
			setPendingMethod(method);
			setStatus(EidEasySignStatus.SETUP_SIGNATURE);
		} else {
			setError('eid_easy_signing.error_method_not_supported_bulk');
			setStatus(EidEasySignStatus.ERROR);
		}
	}, []);

	const confirmSignature = useCallback(
		async (signatureDataUrl: string, source: DrawnSignatureSource) => {
			if (!pendingMethod) {
				return;
			}
			// A refused start leaves no target. Submitting here would send `{}`, which the
			// server reads as "sign everything you can" — the opposite of a refusal.
			if (!target || hasNothingLeftToSign) {
				setError('eid_easy_signing.error_no_documents_selected');
				setStatus(EidEasySignStatus.ERROR);
				return;
			}
			if (pendingMethod === EidEasySigningMethod.FTN) {
				setPendingMethod(null);
				await ftn.confirmFtn(signRequestFields, signatureDataUrl);
			} else {
				setCapturedSignature({ base64: signatureDataUrl, source });
				setStatus(EidEasySignStatus.OTP_INPUT);
			}
		},
		[pendingMethod, ftn, signRequestFields, target, hasNothingLeftToSign]
	);

	const confirmOtp = useCallback(
		async (channel: SignerContactType, value: string) => {
			if (!capturedSignature) {
				return;
			}
			if (!target || hasNothingLeftToSign) {
				setError('eid_easy_signing.error_no_documents_selected');
				setStatus(EidEasySignStatus.ERROR);
				return;
			}
			const sig = capturedSignature;
			setCapturedSignature(null);
			setPendingMethod(null);
			await otp.confirmOtp(signRequestFields, sig.base64, channel, value);
		},
		[capturedSignature, otp, signRequestFields, target, hasNothingLeftToSign]
	);

	const submitOtpCode = useCallback((code: string) => otp.submitOtpCode(code), [otp]);
	const resendOtpCode = useCallback(() => otp.resendOtpCode(), [otp]);

	const reset = useCallback(() => {
		ftn.stopPolling();
		otp.reset();
		setStatus(EidEasySignStatus.IDLE);
		setError(null);
		setProgress(null);
		setPendingMethod(null);
		setCapturedSignature(null);
		setTarget(null);
		setOtpVerifying(false);
		setOtpCodeError(null);
	}, [ftn, otp]);

	/**
	 * The error step's "try again" routes here. Safe to retry even after a stale-selection
	 * refusal: the round is re-checked against the refreshed list on the way out, so the dead
	 * id is dropped rather than resubmitted.
	 */
	const goBack = useCallback(() => setStatus(EidEasySignStatus.METHOD_SELECT), []);

	const otpContext = otp.getOtpContext();

	return {
		status,
		challengeCode,
		error,
		progress,
		otpChannel: otpContext?.channel,
		otpMaskedContact: otpContext?.maskedContact,
		otpVerifying,
		otpCodeError,
		isSigningInProgress:
			status === EidEasySignStatus.PREPARING ||
			status === EidEasySignStatus.AWAITING_SIGN ||
			status === EidEasySignStatus.COMPLETING,
		isModalOpen:
			status === EidEasySignStatus.METHOD_SELECT ||
			status === EidEasySignStatus.SETUP_SIGNATURE ||
			status === EidEasySignStatus.OTP_INPUT ||
			status === EidEasySignStatus.OTP_CODE_ENTRY ||
			status === EidEasySignStatus.PREPARING ||
			status === EidEasySignStatus.AWAITING_SIGN ||
			status === EidEasySignStatus.COMPLETING ||
			status === EidEasySignStatus.DONE ||
			status === EidEasySignStatus.ERROR,
		startBulkSign,
		selectMethod,
		confirmSignature,
		goBack,
		confirmOtp,
		submitOtpCode,
		resendOtpCode,
		reset,
	};
};
